import { useState } from 'react'

import { useFacilitySearchOptions } from '@/hooks/useFacilitySearchOptions'
import { useToasts } from '@/hooks/useToasts'
import { SubmitHandler } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  CaseDocument,
  useUpdateCaseDetailsMutation,
  useUsersQuery,
} from '@/graphql/generated/hooks'
import { getUserOptions } from '@/utils/forms/optionBuilders'
import { tVar } from '@/utils/i18n'

import { UpdateCaseFormInputsIProps } from '../types/types'

interface UseUpdateCaseIProps {
  onClose: () => void
  caseId: string
}

export const useUpdateCase = ({ onClose, caseId }: UseUpdateCaseIProps) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const { showSuccess, showError } = useToasts()

  const { data: optionsData, loading: isUsersLoading } = useUsersQuery({
    fetchPolicy: 'network-only',
    variables: {
      filter: { roleNameIn: ['OPERATOR', 'PROGRAM_MANAGER'] },
    },
  })
  const {
    facilityOptions,
    isFacilitiesLoading,
    isFacilitiesError,
    setFacilityName,
  } = useFacilitySearchOptions()

  const [updateCase] = useUpdateCaseDetailsMutation()

  const userOptions = getUserOptions(optionsData)

  const onUpdateCase: SubmitHandler<UpdateCaseFormInputsIProps> = async (
    values
  ) => {
    setIsLoading(true)
    try {
      await updateCase({
        variables: {
          caseId: caseId,
          caseName: values?.name,
          caseFacilityIds: values?.facilities?.map((f) => f.value) || [],
          caseOwnerId: values?.owner?.value,
          status: values?.status?.value,
        },
        refetchQueries: [
          {
            query: CaseDocument,
            variables: { id: caseId },
          },
        ],
      })
      showSuccess(
        tVar(t, 'cases:crud_update_case_success', { name: values?.name })
      )
      setIsLoading(false)
      onClose()
    } catch (error) {
      showError(error.message)
      setIsLoading(false)
    }
  }

  return {
    facilityOptions,
    userOptions,
    isLoading,
    isFacilitiesLoading,
    isFacilitiesError,
    isUsersLoading,
    onUpdateCase,
    setFacilityName,
  }
}
